import { useEffect, useMemo, useState } from 'react'

import ROUTES from 'src/config/routes.json'
import { getLocationHref } from 'src/utils/browserLocation'

// Default exclusions to prevent legal → legal back loops and auth redirect issues
const DEFAULT_EXCLUDE_PATHS = [
  ROUTES.TERMS,
  ROUTES.PRIVACY,
  ROUTES.SWEEPSTAKE_RULES,
  ROUTES.RESPONSIBLE_PLAY,
  ROUTES.COOKIE_POLICY,
  ROUTES.RIGHT_TO_OPT_OUT,
  ROUTES.STATIC_AUTH_REDIRECT, // Exclude auth redirect page to prevent back button issues
]

type UseBackUrlOptions = {
  backHref?: string
  fallbackUrl?: string
  excludePaths?: string[]
}

/**
 * Custom hook for determining the back URL for legal pages
 * Priority: 1) backHref prop, 2) document.referrer (same domain), 3) fallback
 */
export default function useBackUrl(options: UseBackUrlOptions = {}) {
  const { backHref, fallbackUrl = ROUTES.HOME, excludePaths = [] } = options
  const [backUrl, setBackUrl] = useState(fallbackUrl)

  const isSafeRelativePath = (value: string) => value.startsWith('/') && !value.startsWith('//')
  const combinedExclude = useMemo(
    () => Array.from(new Set([...DEFAULT_EXCLUDE_PATHS, ...excludePaths])),
    [excludePaths],
  )

  useEffect(() => {
    let nextUrl: string | null = null

    // Priority 1: Use provided backHref (only if safe and internal)
    if (backHref && isSafeRelativePath(backHref)) {
      const shouldExclude = combinedExclude.some(
        (excludePath) => backHref === excludePath || backHref.startsWith(excludePath + '/'),
      )

      if (!shouldExclude) {
        nextUrl = backHref
      }
    }

    // Priority 2: Try to use document.referrer
    if (!nextUrl && typeof window !== 'undefined' && document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer)
        const currentUrl = new URL(getLocationHref())

        // Only use referrer if it's from the same domain
        if (referrerUrl.origin === currentUrl.origin) {
          const referrerPath = referrerUrl.pathname

          // Check if referrer path should be excluded
          const shouldExclude = combinedExclude.some(
            (excludePath) => referrerPath === excludePath || referrerPath.startsWith(excludePath + '/'),
          )

          if (!shouldExclude) {
            const referrerInternal = referrerUrl.pathname + referrerUrl.search + referrerUrl.hash
            nextUrl = referrerInternal
          }
        }
      } catch (error) {
        // If referrer is invalid, explicitly route to HOME
        nextUrl = ROUTES.HOME
      }
    }

    // Priority 3: Use fallback if nothing else set
    const finalUrl = nextUrl ?? fallbackUrl
    setBackUrl((prev) => (prev === finalUrl ? prev : finalUrl))
  }, [backHref, fallbackUrl, combinedExclude])

  return backUrl
}
